import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import JplModal from './JplModal';
import { PageHeader, Banner, IconButton, WideSection, ICONS } from './ui';

// Base columns from RCPTimeSummaryBillableInfo. Custom per-user Job columns are
// inserted right after "Billable" at render time.
const BASE_COLUMNS = [
  { key: 'Name', header: 'Name', format: 'text', sortKey: 'No_' },
  { key: 'PeriodName', header: 'Period Name', format: 'text', sortKey: 'ColorVal' },
  { key: 'Overhead', header: 'General Overhead', format: 'pct1' },
  { key: 'Vac-Hol-Other', header: 'Vac / Hol / Other OH', format: 'pct1' },
  { key: 'BusDev', header: 'Bus Dev', format: 'pct1' },
  { key: 'Billable', header: 'Billable', format: 'pct1' },
  { key: 'PTO', header: 'PTO', format: 'num2' },
  { key: 'PeriodHours', header: "Employee's Period Hours", format: 'num2' },
  { key: 'TotalHours', header: 'Calendar Actual Hours', format: 'num0' },
];

function formatValue(value, format) {
  if (value == null || value === '') return '';
  const num = parseFloat(value);
  switch (format) {
    case 'pct1':
      return isNaN(num) ? String(value) : `${(num * 100).toFixed(1)}%`;
    case 'num2':
      return isNaN(num) ? String(value) : num.toFixed(2);
    case 'num0':
      return isNaN(num) ? String(value) : num.toFixed(0);
    case 'text':
    default:
      return String(value);
  }
}

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// Key a per-job breakdown row by resource + period + job.
const jobKey = (resourceNo, periodName, jobNo) => `${resourceNo}||${periodName}||${jobNo}`;

// A custom Job column's name defaults to the job description, falling back to
// the job number when the job has no description.
const defaultColLabel = (jobNo, jobDescription) => (jobDescription || '').trim() || String(jobNo || '');

export default function BillableTime() {
  const { user, refreshUser } = useAuth();
  // When emulating, the effective Navision resource is the emulated user's
  // (the server's billable endpoints already run as that user). Fall back to
  // the signed-in user's own resource otherwise.
  const myResource = (user && user.emulating && user.emulating.resourceNo) || user?.resourceNo || '';

  const [rows, setRows] = useState(null);
  const [byJobRows, setByJobRows] = useState([]);
  const [byJobAvailable, setByJobAvailable] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState([]); // [{ jobNo, jobDescription }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Edit modal state
  const [showEdit, setShowEdit] = useState(false);
  const [editJobs, setEditJobs] = useState([]);
  const [pickVal, setPickVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Self-service emulation: click another user's row to view/manage their
  // timesheets & expenses. Only offered when not already emulating.
  const [emulatingBusy, setEmulatingBusy] = useState(false);
  const canEmulate = !(user && user.emulating);
  // JPL "View Entries" modal for a person (resource), opened from their name link.
  const [jplResource, setJplResource] = useState(null); // { resource, resourceName }
  const emulateRow = async (row) => {
    if (emulatingBusy) return;
    const label = row.Name || row.No_;
    if (!window.confirm(`View ${label}'s timesheets and expenses (emulate this user)?`)) return;
    setEmulatingBusy(true);
    try {
      await api.billableEmulate(row.No_, row.Name || '');
      await refreshUser(); // App re-keys pages; this component remounts as the emulated user.
    } catch (e) {
      alert(e.message || 'Failed to emulate user');
      setEmulatingBusy(false);
    }
  };

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.getBillableInfo(),
      api.getBillableInfoByJob().then((r) => ({ ok: true, r })).catch((e) => ({ ok: false, e })),
      api.getBillableColumns().then((r) => r).catch(() => ({ columns: [] })),
    ])
      .then(([summary, byJob, cols]) => {
        if (cancelled) return;
        setRows(summary.rows || []);
        if (byJob.ok) {
          setByJobRows(byJob.r.rows || []);
          setByJobAvailable(true);
        } else {
          setByJobRows([]);
          setByJobAvailable(false);
        }
        setSelectedJobs((cols.columns || []).map((c) => {
          const jobNo = c.JobNo != null ? String(c.JobNo) : String(c.jobNo || '');
          const jobDescription = c.JobDescription != null ? String(c.JobDescription) : String(c.jobDescription || '');
          const savedLabel = (c.Label != null ? String(c.Label) : (c.label != null ? String(c.label) : '')).trim();
          return { jobNo, jobDescription, label: savedLabel || defaultColLabel(jobNo, jobDescription) };
        }).filter((c) => c.jobNo));
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => load(), [load]);

  // Index the per-job breakdown by resource+period+job for quick lookup.
  const byJobIndex = useMemo(() => {
    const m = new Map();
    for (const r of byJobRows) {
      m.set(jobKey(r.No_, r.PeriodName, r.JobNo), r);
    }
    return m;
  }, [byJobRows]);

  // Jobs already included in THIS user's calculations (their own resource rows).
  const availableJobs = useMemo(() => {
    const seen = new Map();
    for (const r of byJobRows) {
      if (String(r.No_) !== String(myResource)) continue;
      const jobNo = r.JobNo != null ? String(r.JobNo) : '';
      if (!jobNo || seen.has(jobNo)) continue;
      seen.set(jobNo, { jobNo, jobDescription: r.JobDescription != null ? String(r.JobDescription) : '' });
    }
    return Array.from(seen.values()).sort((a, b) => a.jobNo.localeCompare(b.jobNo));
  }, [byJobRows, myResource]);

  // Column list = base columns, with the custom Job columns inserted after Billable.
  const columns = useMemo(() => {
    const jobCols = selectedJobs.map((j) => ({
      key: `job:${j.jobNo}`,
      header: (j.label && j.label.trim()) || defaultColLabel(j.jobNo, j.jobDescription),
      title: j.jobDescription ? `${j.jobNo} — ${j.jobDescription}` : j.jobNo,
      format: 'pct1',
      isJob: true,
    }));
    if (jobCols.length === 0) return BASE_COLUMNS;
    const out = [];
    for (const c of BASE_COLUMNS) {
      out.push(c);
      if (c.key === 'Billable') out.push(...jobCols);
    }
    return out;
  }, [selectedJobs]);

  // Apply the custom Job columns: for each row, move each selected job's hours
  // out of the category columns it lands in and into its own column (as a % of
  // standard hours, matching the Billable-family columns). Period Hours / Actual
  // Hours are totals/denominators and are left unchanged.
  const displayRows = useMemo(() => {
    if (!rows) return null;
    if (selectedJobs.length === 0) return rows;
    return rows.map((row) => {
      const d = num(row.TotalHours); // standard-hours divisor for this period
      const out = { ...row };
      let billable = num(row.Billable);
      let overhead = num(row.Overhead);
      let busDev = num(row.BusDev);
      let vac = num(row['Vac-Hol-Other']);
      let pto = num(row.PTO);
      for (const j of selectedJobs) {
        const jr = byJobIndex.get(jobKey(row.No_, row.PeriodName, j.jobNo));
        if (!jr) { out[`job:${j.jobNo}`] = 0; continue; }
        const jobPct = d ? num(jr.PeriodHours) / d : 0;
        out[`job:${j.jobNo}`] = jobPct;
        if (d) {
          billable -= num(jr.Billable) / d;
          overhead -= num(jr.Overhead) / d;
          busDev -= num(jr.BusDev) / d;
          vac -= num(jr['Vac-Hol-Other']) / d;
        }
        pto -= num(jr.PTO);
      }
      out.Billable = billable;
      out.Overhead = overhead;
      out.BusDev = busDev;
      out['Vac-Hol-Other'] = vac;
      out.PTO = pto;
      return out;
    });
  }, [rows, selectedJobs, byJobIndex]);

  const sortedRows = useMemo(() => {
    if (!displayRows) return null;
    const data = [...displayRows];
    if (sortCol) {
      const col = columns.find((c) => c.key === sortCol);
      const key = col?.sortKey || sortCol;
      const dir = sortDir === 'asc' ? 1 : -1;
      data.sort((a, b) => {
        const aIsMe = String(a.No_ || '') === String(myResource) ? 0 : 1;
        const bIsMe = String(b.No_ || '') === String(myResource) ? 0 : 1;
        if (aIsMe !== bIsMe) return aIsMe - bIsMe;
        const aVal = (a[key] || '').toString();
        const bVal = (b[key] || '').toString();
        return aVal.localeCompare(bVal) * dir;
      });
    } else {
      data.sort((a, b) => {
        const aIsMe = String(a.No_ || '') === String(myResource) ? 0 : 1;
        const bIsMe = String(b.No_ || '') === String(myResource) ? 0 : 1;
        if (aIsMe !== bIsMe) return aIsMe - bIsMe;
        const grpA = (a.ResourceGroupNo || '').toString();
        const grpB = (b.ResourceGroupNo || '').toString();
        if (grpA !== grpB) return grpA.localeCompare(grpB);
        const empA = (a.No_ || '').toString();
        const empB = (b.No_ || '').toString();
        if (empA !== empB) return empA.localeCompare(empB);
        return (num(a.PeriodHours)) - (num(b.PeriodHours));
      });
    }
    return data;
  }, [displayRows, columns, sortCol, sortDir, myResource]);

  const handleHeaderClick = (col) => {
    if (!col.sortKey) return;
    if (sortCol === col.key) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortCol(null); setSortDir('asc'); }
    } else {
      setSortCol(col.key);
      setSortDir('asc');
    }
  };

  const openEdit = () => {
    setEditJobs(selectedJobs.map((j) => ({ ...j })));
    setPickVal('');
    setSaveError(null);
    setShowEdit(true);
  };

  const addPicked = () => {
    if (!pickVal) return;
    const job = availableJobs.find((j) => j.jobNo === pickVal);
    if (!job) return;
    if (editJobs.some((j) => j.jobNo === job.jobNo)) return;
    setEditJobs([...editJobs, { ...job, label: defaultColLabel(job.jobNo, job.jobDescription) }]);
    setPickVal('');
  };

  const removeEditJob = (jobNo) => setEditJobs(editJobs.filter((j) => j.jobNo !== jobNo));

  const setEditLabel = (jobNo, label) =>
    setEditJobs(editJobs.map((j) => (j.jobNo === jobNo ? { ...j, label } : j)));

  const saveEdit = async (jobsOverride) => {
    const jobs = jobsOverride != null ? jobsOverride : editJobs;
    setSaving(true);
    setSaveError(null);
    try {
      const normalized = jobs.map((j) => ({
        jobNo: j.jobNo,
        jobDescription: j.jobDescription,
        label: (j.label && j.label.trim()) || defaultColLabel(j.jobNo, j.jobDescription),
      }));
      await api.saveBillableColumns(normalized.map((j) => ({ jobNo: j.jobNo, jobDescription: j.jobDescription, label: j.label })));
      setSelectedJobs(normalized);
      setShowEdit(false);
    } catch (e) {
      setSaveError(e.message || 'Failed to save columns');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner" />Loading billable time...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '8px 16px' }}>
        <Banner kind="err">{error}</Banner>
      </div>
    );
  }

  const pickableJobs = availableJobs.filter((j) => !editJobs.some((s) => s.jobNo === j.jobNo));

  return (
    <WideSection fill style={{ paddingBottom: 16 }}>
      <div style={{ padding: '0 4px' }}>
        <PageHeader
          title="Billable %"
          actions={(
            <>
              <button className="btn btn-outline btn-sm"
                onClick={openEdit}
                disabled={!byJobAvailable}
                title={byJobAvailable ? 'Add or remove specific-job columns' : 'Per-job data is not available yet'}>
                {ICONS.edit} Edit Columns
              </button>
              {selectedJobs.length > 0 && (
                <button className="btn btn-outline btn-sm"
                  onClick={() => saveEdit([])} disabled={saving}
                  title="Remove all custom job columns and return to the original view">
                  Reset View
                </button>
              )}
            </>
          )}
        />
        {!byJobAvailable && (
          <span style={{ fontSize: '11px', color: 'var(--gray-400)' }}>
            Per-job columns unavailable (RCPTimeSummaryBillableInfoByJob not installed on Navision).
          </span>
        )}
      </div>

      <div style={{ fontSize: '11px', color: 'var(--gray-400)', padding: '0 4px 8px' }}>
        Click a name to see that person's entries.
        {canEmulate && (sortedRows || []).some((r) => String(r.No_ || '') !== String(myResource)) &&
          ' Use Emulate to view and manage someone else’s timesheets and expenses.'}
      </div>

      {!sortedRows || sortedRows.length === 0 ? (
        <div className="empty-state"><p>No billable time data available.</p></div>
      ) : (
        <div className="tabular-scroll">
          <table className="tabular-table billable-table">
            <thead>
              <tr>
                {columns.map((col) => {
                  const sortable = !!col.sortKey;
                  const active = sortCol === col.key;
                  return (
                    <th key={col.key}
                      onClick={sortable ? () => handleHeaderClick(col) : undefined}
                      title={col.title || undefined}
                      style={{
                        padding: '6px 6px',
                        textAlign: col.format !== 'text' ? 'right' : 'left',
                        fontSize: '11px',
                        fontWeight: 700,
                        whiteSpace: 'normal',
                        cursor: sortable ? 'pointer' : undefined,
                        userSelect: sortable ? 'none' : undefined,
                        background: col.isJob ? '#eef2ff' : undefined,
                      }}
                    >
                      {col.header}
                      {sortable && (
                        <span style={{ marginLeft: '4px', fontSize: '10px', color: active ? 'var(--primary)' : 'var(--gray-300)' }}>
                          {active ? (sortDir === 'asc' ? '▲' : '▼') : '▴'}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, i) => {
                const isMe = String(row.No_ || '') === String(myResource);
                const stripe = i % 2 === 1 ? '#f9fafb' : 'transparent';
                const bg = isMe ? '#eff6ff' : stripe;
                const canViewEntries = !!row.No_;
                const showEmulate = canEmulate && !isMe && !!row.No_;
                return (
                  <tr key={i} style={{ background: bg, fontWeight: isMe ? 600 : 'normal' }}>
                    {columns.map((col) => {
                      if (col.key === 'Name') {
                        return (
                          <td key={col.key} style={{ padding: '4px 6px', textAlign: 'left', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              {canViewEntries ? (
                                <button type="button"
                                  onClick={() => setJplResource({ resource: String(row.No_), resourceName: row.Name || String(row.No_) })}
                                  title="View this person's entries"
                                  style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer' }}>
                                  {formatValue(row.Name, col.format)}
                                </button>
                              ) : (
                                formatValue(row.Name, col.format)
                              )}
                              {showEmulate && (
                                <button type="button"
                                  className="btn btn-outline btn-sm"
                                  disabled={emulatingBusy}
                                  onClick={() => emulateRow(row)}
                                  title={`View and manage ${row.Name || row.No_}'s timesheets and expenses`}
                                  style={{ fontSize: '10px', padding: '1px 6px', lineHeight: 1.4 }}>
                                  Emulate
                                </button>
                              )}
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td key={col.key} style={{
                          padding: '4px 6px',
                          textAlign: col.format !== 'text' ? 'right' : 'left',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          background: col.isJob ? (isMe ? undefined : '#fafbff') : undefined,
                        }}>
                          {formatValue(row[col.key], col.format)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showEdit && (
        <div className="modal-overlay" onClick={() => setShowEdit(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <strong>Edit Billable Columns</strong>
              <IconButton icon="close" title="Close" onClick={() => setShowEdit(false)} />
            </div>
            <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginBottom: '12px' }}>
              Add a column for a specific job. Its hours are shown separately and removed from the
              other columns (e.g. Billable) where they would otherwise be counted. Only jobs already
              in your billable calculation are listed. This changes only your view.
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Add a job column</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select className="form-control" value={pickVal} onChange={(e) => setPickVal(e.target.value)}
                  disabled={pickableJobs.length === 0}>
                  <option value="">{pickableJobs.length === 0 ? 'No more jobs available' : 'Select a job…'}</option>
                  {pickableJobs.map((j) => (
                    <option key={j.jobNo} value={j.jobNo}>
                      {j.jobDescription ? `${j.jobNo} — ${j.jobDescription}` : j.jobNo}
                    </option>
                  ))}
                </select>
                <button className="btn btn-primary" style={{ flex: '0 0 auto' }} onClick={addPicked} disabled={!pickVal}>Add</button>
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Current job columns ({editJobs.length})</label>
              {editJobs.length === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--gray-400)', padding: '6px 0' }}>None — showing the original view.</div>
              ) : (
                <>
                  <div style={{ fontSize: '11px', color: 'var(--gray-400)', margin: '2px 0 6px' }}>
                    Edit a column's name below (defaults to the job description).
                  </div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {editJobs.map((j) => (
                      <li key={j.jobNo} style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <input className="form-control" style={{ flex: 1, fontSize: '12px' }}
                            value={j.label != null ? j.label : ''}
                            placeholder={defaultColLabel(j.jobNo, j.jobDescription)}
                            onChange={(e) => setEditLabel(j.jobNo, e.target.value)}
                            aria-label={`Column name for job ${j.jobNo}`} />
                          <IconButton icon="close" title="Remove column" onClick={() => removeEditJob(j.jobNo)} style={{ color: '#b91c1c', flex: '0 0 auto' }} />
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--gray-400)', marginTop: '2px' }}>
                          {j.jobDescription ? `${j.jobNo} — ${j.jobDescription}` : j.jobNo}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {saveError && <div style={{ color: '#b91c1c', fontSize: '12px', marginBottom: '8px' }}>{saveError}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              <button className="btn btn-outline" style={{ fontSize: '12px' }} onClick={() => setEditJobs([])} disabled={editJobs.length === 0 || saving}
                title="Clear all custom columns (reset to original view)">Reset to original</button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-outline" onClick={() => setShowEdit(false)} disabled={saving}>Cancel</button>
                <button className="btn btn-primary" onClick={() => saveEdit()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {jplResource && (
        <JplModal
          resource={jplResource.resource}
          resourceName={jplResource.resourceName}
          onClose={() => setJplResource(null)}
        />
      )}
    </WideSection>
  );
}
